import { FC, HTMLProps } from 'react';
import styles from './ImageModal.module.scss';

import { Icon } from 'components/Icon';
import { generateId } from 'util/generateId';
import { useRefHandleFocus } from 'hooks/useHandleFocus';
import { useWindowSize } from 'hooks/breakpoints';
import { SplitLayout } from 'components/Layout/SplitLayout/SplitLayout';
import { VisualHeaderLayout } from 'components/ModalKit/VisualHeaderLayout/VisualHeaderLayout';
import { StackedCtaLayout } from 'components/ModalKit/Cta/StackedCtaLayout';
import CtaItem from 'components/ModalKit/Cta/CtaItem';
import { ThreePartLayout } from 'components/Layout/ThreePartLayout/ThreePartLayout';
import { ModalKitModal } from 'components/ModalKit/ModalKitModal/ModalKitModal';
import { ModalKitCloseButton } from 'components/ModalKit/ModalKitCloseButton/ModalKitCloseButton';
import Typography from './Typography/Typography';

export type ImageModalProps = HTMLProps<HTMLElement> & {
  isOpen: boolean;
  className?: string;
  primaryButtonContent?: string;
  secondaryButtonContent?: string;
  iconName: string;
  iconSize?: number;
  title?: string;
  subtitle?: string;
  onClick?: () => void;
  onClose: () => void;
  testId?: string;
  from?: string;
};

export const ImageModal: FC<ImageModalProps> = ({
  isOpen,
  className,
  onClick,
  onClose,
  iconName,
  iconSize = 200,
  title,
  subtitle,
  primaryButtonContent,
  secondaryButtonContent,
  children,
  from,
  testId = 'image-modal',
  ...props
}) => {
  const { isDesktop } = useWindowSize();

  const showPrimary = primaryButtonContent && onClick;
  const showSecondary = secondaryButtonContent && onClose;
  const labelId = generateId();
  const PRIMARY_TEST_ID = `${testId}-primary-button`;
  const SECONDARY_TEST_ID = `${testId}-secondary-button`;
  const [ref] = useRefHandleFocus(isOpen);

  const mobileLayout = (
    <ThreePartLayout className={styles.mobileLayout}>
      <ThreePartLayout.Header>
        <VisualHeaderLayout
          className={!isDesktop && from === 'onSignInClick' ? styles.mobileHeaderClass : styles.header}
          iconName = {!isDesktop ? iconName : undefined}
          iconSize = {!isDesktop? iconSize : undefined}
        >
          <VisualHeaderLayout.Title>
            <div className={!isDesktop && from === 'onSignInClick' ? styles.mobileTitleClass : ''}>
              <Typography.Title element="h2" id={labelId}>{title}</Typography.Title>
            </div>
          </VisualHeaderLayout.Title>
          <VisualHeaderLayout.Subtitle>
            <Typography.Body>{subtitle}</Typography.Body>
          </VisualHeaderLayout.Subtitle>
        </VisualHeaderLayout>
      </ThreePartLayout.Header>
      <ThreePartLayout.Main>{children}</ThreePartLayout.Main>
      <ThreePartLayout.Footer>
        <StackedCtaLayout className={styles.footer}>
          {showPrimary && (
            <CtaItem.Primary onClick={onClick} ref={ref} data-testid={PRIMARY_TEST_ID}>
              {primaryButtonContent}
            </CtaItem.Primary>
          )}
          {showSecondary && (
            <CtaItem.Secondary
              onClick={onClose}
              data-testid={SECONDARY_TEST_ID}
            >
              {secondaryButtonContent}
            </CtaItem.Secondary>
          )}
        </StackedCtaLayout>
      </ThreePartLayout.Footer>
    </ThreePartLayout>
  );

  const desktopLayout = (
    <SplitLayout className={styles.desktopLayout}>
      <SplitLayout.Left>{mobileLayout}</SplitLayout.Left>
      <SplitLayout.Right>
        <div className={styles.imageWrapper}>
          <Icon name={iconName} height={400} width={400} />
        </div>
      </SplitLayout.Right>
    </SplitLayout>
  );

  return (
    <ModalKitModal
      aria={{ labelledby: labelId }}
      isOpen={isOpen}
      onClose={onClose}
      testId={testId}
    >
      {onClose && <ModalKitCloseButton onClick={onClose} />}

      {isDesktop && desktopLayout}
      {!isDesktop && mobileLayout}
    </ModalKitModal>
  );
};
